import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import { message } from '@bb/elements-web';
import {
  getKeyholderBillingProfile,
  getReactivationPrices,
} from 'api/SupraController';
import { SupraController } from 'api';
import { AuthContext } from 'contexts';
import type { IKeyholderDataWeb } from '@/app-package';
import { ViewingLevel } from '.';

// ============================================================================
// Types
// ============================================================================

interface SubscriptionsPageContextShape {
  /** The credit card billing info for this user. */
  billingProfile: IKeyholderDataWeb;
  /** Function to fetch the billing profile. */
  refetchBillingProfile: () => Promise<void>;
  /** Whether the fees for a user with an overdue balance are being fetched. */
  loadingOverdueFees: 'loading' | 'done' | 'error';
  /** The prorated price a user must pay to re-activate an overdue subscription. */
  proratedPricing: number | undefined;
  /** The extra fee amount a user has to pay to re-activate their subscription after it has been overdue for too long. */
  reinstatementFee: number | undefined;
  /** Function to update the billing profile */
  updateBillingProfile: (
    updatedBillingProfile: IKeyholderDataWeb,
    resetPaymentStatus?: boolean,
  ) => Promise<void>;
  /** The error encountered while updating the billing profile details. */
  updateProfileError: Error | undefined;
  /** The error encountered while fetching the billing profile details. */
  fetchProfileError: Error | undefined;
  /** Whether we are still fetching the billing profile details. */
  isFetchingProfile: boolean;
  /** Whether we are still updating the billing profile details. */
  isUpdatingProfile: boolean;
  /** Whether the user has a saved credit card on file. */
  hasSavedCard: boolean;
}

interface SubscriptionsPageProviderProps {
  billingCustomerNumber?: string;
  children: React.ReactNode;
  orgId: string;
  viewingLevel: ViewingLevel;
}

/**
 * Indicates (via `response`) whether the user has permission
 * to manage some aspect of the billing profile.
 *
 * If the user does not have permission, the `message` field
 * will contain the reason why.
 */
export type CanManageBillingProfileType =
  | { response: true }
  | { response: false; message: string };

/**
 * Indicates (via `response`) whether the user has an overdue
 * balance that is still unpaid.
 *
 * If the user is overdue by some amount, the `message` field
 * will display the confirmation for the user to agree to pay the
 * amount.
 */
export type OverdueBalanceType =
  | { response: false }
  | { response: true; message: React.ReactNode };

// ============================================================================
// Context
// ============================================================================

const SubscriptionsPageContext =
  createContext<SubscriptionsPageContextShape | null>(null);

/**
 * Hook to consume SubscriptionsPageContext.
 * Throws error if used outside of provider.
 */
export function useSubscriptionsPageContext(): SubscriptionsPageContextShape {
  const context = useContext(SubscriptionsPageContext);
  if (!context) {
    throw new Error(
      'useSubscriptionsPageContext must be used within a SubscriptionsPageProvider.',
    );
  }
  return context;
}

// ============================================================================
// Provider
// ============================================================================

/**
 * Provides shared billing profile data to all components on the Subscriptions & Payments page.
 * Fetches billing profile once and shares it with child components.
 */
export const SubscriptionsPageProvider: React.FC<
  SubscriptionsPageProviderProps
> = ({ billingCustomerNumber, children, orgId, viewingLevel }) => {
  const { auth: user, fetchAuth } = useContext(AuthContext);

  const [billingProfile, setBillingProfile] = useState<IKeyholderDataWeb>();
  const [updateProfileError, setUpdateProfileError] = useState<Error>();
  const [fetchProfileError, setFetchProfileError] = useState<Error>();
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isFetchingProfile, setIsFetchingProfile] = useState(false);

  const [loadingOverdueFees, setLoadingOverdueFees] = useState<
    'loading' | 'done' | 'error'
  >('done');
  const [proratedPricing, setProratedPricing] = useState<number | undefined>(
    undefined,
  );
  const [reinstatementFee, setReinstatementFee] = useState<number | undefined>(
    undefined,
  );

  const fetchBillingProfile = useCallback(async () => {
    if (billingCustomerNumber === undefined) {
      setBillingProfile(null);
      setFetchProfileError(new Error('No Billing Customer Number provided'));
      return;
    }

    if (orgId === undefined) {
      setBillingProfile(null);
      setFetchProfileError(new Error('No org ID provided'));
      return;
    }

    setFetchProfileError(null);

    try {
      setIsFetchingProfile(true);
      const profile = await getKeyholderBillingProfile({
        orgId,
        billingCustomerNumber,
      });

      setBillingProfile(profile);
    } catch (error) {
      const err =
        error instanceof Error
          ? error
          : new Error('Failed to fetch billing profile');
      setFetchProfileError(err);
      setBillingProfile(null);
      message.error('Failed to load billing information.');
    } finally {
      setIsFetchingProfile(false);
    }
  }, [orgId, billingCustomerNumber]);

  useEffect(() => {
    fetchBillingProfile();
  }, [fetchBillingProfile]);

  const hasSavedCard = useMemo(
    () => Boolean(billingProfile?.CCNumber && billingProfile?.CardholderName),
    [billingProfile?.CCNumber, billingProfile?.CardholderName],
  );

  const updateBillingProfile = async (
    updatedBillingProfile: IKeyholderDataWeb,
    resetPaymentStatus?: boolean,
  ) => {
    if (!updatedBillingProfile) {
      setUpdateProfileError(new Error('No billing profile provided'));
      return;
    }

    setIsUpdatingProfile(true);
    const profile = await SupraController.updateKeyholderBillingProfile({
      billingProfile: updatedBillingProfile,
      resetPaymentStatus: resetPaymentStatus ?? false,
    }).catch(error => {
      // Set error state based on the error returned from the API
      if ('OperationMessage' in error) {
        setUpdateProfileError(new Error(error.OperationMessage));
      } else if (error instanceof Error) {
        setUpdateProfileError(error);
      } else {
        setUpdateProfileError(
          new Error(
            'Unable to update billing profile. Please try again later.',
          ),
        );
      }

      // Ensure loading state is set to false
      setIsUpdatingProfile(false);

      // Re-throw the error to be caught by the caller
      throw error;
    });

    // Update state
    await fetchAuth();
    setBillingProfile(profile);
    setUpdateProfileError(undefined);
    setIsUpdatingProfile(false);
  };

  useEffect(() => {
    // TODO: add org/brokerage implementation for reactivation prices

    if (viewingLevel === 'user') {
      if (user.isInBillingCollection === true) {
        // Grab the prices to display if the user's billing is In Collection
        const fetchReactivationPrices = async (): Promise<void> => {
          setLoadingOverdueFees('loading');
          try {
            const {
              reinstatementFee: fetchedReinstatementFee,
              proratedPricing: fetchedProratedPricing,
            } = await getReactivationPrices({ userId: user.id });

            // Return everything
            setReinstatementFee(fetchedReinstatementFee);
            setProratedPricing(fetchedProratedPricing);
            setLoadingOverdueFees('done');
          } catch {
            // Let the user know if anything goes wrong
            message.error(
              `Encountered error while fetching the data for your prorated pricing. Please contact the Supra customer success team.`,
            );
            setLoadingOverdueFees('error');
          }
        };
        fetchReactivationPrices();
      }
    }
  }, [user.id, user.isInBillingCollection, viewingLevel]);

  return (
    <SubscriptionsPageContext.Provider
      value={{
        billingProfile,
        refetchBillingProfile: fetchBillingProfile,
        loadingOverdueFees,
        proratedPricing,
        reinstatementFee,
        updateBillingProfile,
        updateProfileError,
        fetchProfileError,
        isFetchingProfile,
        isUpdatingProfile,
        hasSavedCard,
      }}
    >
      {children}
    </SubscriptionsPageContext.Provider>
  );
};

export default SubscriptionsPageContext;
